<template>
  <!-- Loader displayed when isLoading is true -->
  <div
    v-if="showLoader"
    class="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50 z-50"
  >
    <svg
      class="animate-spin h-16 w-16 text-white"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        stroke-width="4"
        fill="none"
      />
    </svg>
  </div>
  <div v-else>
    <div class="relative overflow-x-auto shadow-md sm:rounded-sm mt-5">
      <table class="w-full text-left rtl:text-right text-gray-700">
        <thead>
          <tr class="bg-gray-100">
            <th
              v-for="(header, index) in tableHeaders"
              :key="index"
              scope="col"
              class="px-6 py-3"
            >
              <div class="flex items-center">
                {{ header }}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in inventoryItems"
            :key="index"
            class="border-b"
          >
            <td class="px-6 py-2">{{ index + 1 }}</td>
            <td class="px-6 py-2">{{ item.code }}</td>
            <td class="px-6 py-2">{{ item.trade_name }}</td>
            <td class="px-6 py-2">
              {{
                item.item_type == 1 ? "Pharamceutical" : "Non-pharmaceutical"
              }}
            </td>
            <td class="px-6 py-2">{{ item.generic_name }}</td>
            <td class="px-6 py-2">{{ item.drug_category?.name }}</td>
            <td class="px-6 py-2">{{ item.drug_dosage_form?.name }}</td>
            <td class="px-6 py-2">
              {{ item.strength }} {{ item.drug_strength_unit?.name }}
            </td>
            <td class="px-6 py-2">
              {{ item.created_at }}
            </td>
            <td class="px-6 py-2">
              <div class="flex space-x-2">
                <button
                  @click="viewInventoryItem(item.id)"
                  class="bg-orange-400 hover:bg-orange-500 text-white px-4 py-1 rounded btn-xs"
                >
                  <View color="white" :size="24" />
                </button>
                <button
                  @click="editInventoryItem(item)"
                  class="btn-xs bg-custom-blue text-white px-4 py-1 rounded flex items-center gap-1"
                >
                  <Edit color="white" :size="24" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <PaginationComponent
      v-model:currentPage="currentPage"
      v-model:itemsPerPage="itemsPerPage"
      :totalPages="totalPages"
      :totalItems="totalItems"
      @update:currentPage="goToPage"
      @update:changePageSize="changePageSize"
    />
    <!-- Modal placeholder -->
    <InventoryQueueModal
      :show="showModal"
      :medicationDetails="selectedMedicationDetails"
      @close="closeDetailsModal"
    />

    <EditPharmaceuticalItem
      :show="showEditDrugModal"
      :inventoryItem="itemToEdit"
      @close="closeEditModal"
    />

    <EditNonPharmaceuticalItem
      :show="showEditNonDrugModal"
      :inventoryItem="itemToEdit"
      @close="closeEditModal"
    />
  </div>
</template>

<script>
import InventoryQueueModal from "@/views/modals/InventoryQueueModal.vue";
import PaginationComponent from "@/components/shared/pagination.vue";
import EditPharmaceuticalItem from "../modals/inventory/EditPharmaceuticalItem.vue";
// ✅ Import the new edit modal for non-pharmaceutical items
import EditNonPharmaceuticalItem from "../modals/inventory/EditNonPharmaceuticalItem.vue";
import { formatDateTime } from "@/services/customUtilities";
import apiClient from "@/services/api";
import { View } from "lucide-vue-next";
import { Edit } from "lucide-vue-next";

export default {
  name: "InventoryQueueTab",
  components: {
    InventoryQueueModal,
    PaginationComponent,
    EditPharmaceuticalItem,
    EditNonPharmaceuticalItem, // ✅ Register the component
    View,
    Edit,
  },
  data() {
    return {
      showModal: false,
      showEditDrugModal: false,
      showEditNonDrugModal: false,
      selectedMedicationDetails: null,
      tableHeaders: [
        "No",
        "Code",
        "Brand Name",
        "Item Type",
        "Generic Name",
        "Medicine Type",
        "Formulation Type",
        "Strength",
        "Created At",
        "Action",
      ],
      medications: [],
      inventoryItems: [],
      drugCategories: [],
      drugUnitStrengths: [],
      druDosageForms: [],
      currentPage: 1,
      itemsPerPage: 10,
      pageSizes: [25, 50, 100],
      showLoader: false,
      itemToEdit: "",
      totalItems: 0,
      totalPages: 0,
    };
  },
  computed: {
    paginatedData() {
      const currentPage = Number(this.currentPage);
      const itemsPerPage = Number(this.itemsPerPage);
      const start = (currentPage - 1) * itemsPerPage;
      const end = start + itemsPerPage;
      return this.inventoryItems.slice(start, end);
    },
  },
  mounted() {
    this.fetchInventoryItems();
  },
  methods: {
    goToPage(pageNumber) {
      console.log("goToPage", pageNumber);
      this.currentPage = pageNumber;
      this.fetchInventoryItems();
    },
    changePageSize(pageSize) {
      console.log("change page size", pageSize);
      this.itemsPerPage = pageSize;
      this.fetchInventoryItems();
    },
    getCategoryStyle(category) {
      switch (category) {
        case "Pharmaceutical":
          return "bg-purple-50 text-purple-700";
        case "Non-pharmaceutical":
          return "bg-green-50 text-green-700";
        default:
          return "bg-gray-50 text-gray-800";
      }
    },
    viewInventoryItem(itemId) {
      this.$router.push("/view-inventory-items/" + itemId);
    },
    editInventoryItem(item) {
      this.itemToEdit = item;
      if (item.item_type == 1) {
        this.showEditDrugModal = true;
      } else {
        this.showEditNonDrugModal = true;
      }
    },
    closeEditModal() {
      this.showEditDrugModal = false;
      this.showEditNonDrugModal = false;
    },
    async fetchInventoryItems() {
      try {
        this.showLoader = true;
        const response = await apiClient.listInventoryItems({
          page: this.currentPage,
          per_page: this.itemsPerPage,
        });
        if (response.success) {
          this.showLoader = false;
          this.inventoryItems = response.data.map((item) => ({
            ...item,
            created_at: formatDateTime(item.created_at),
          }));
          this.totalItems = response.meta.total_items;
          this.totalPages = response.meta.total_pages;
          this.itemsPerPage = response.meta.per_page;
        }
      } catch (error) {
        console.log("errors", error);
        this.showLoader = false;
        this.$toast.error("Failed to fetch inventory items");
      }
    },
    async searchInventoryItems(params) {
      try {
        this.showLoader = true;
        const response = await apiClient.searchInventoryItems(params);

        if (response.success) {
          this.inventoryItems = [];
          this.inventoryItems = response.data;
        }

        this.showLoader = false;
      } catch (error) {
        this.$toast.error(
          "Unable to search inventory, please check your internet connection."
        );
      }
    },
  },
};
</script>

<style scoped>
/* Add any additional styles here if needed */
</style>